import React, { useCallback, useMemo, useRef, useState } from "react";
import Plot from "./PlotlyChart";
import { processBinData } from "../../graphView/processBinData";
import {
  plotlyAxisBase,
  plotlyAxisBaseCategoricalX,
  plotlyAxisTitle,
  plotlyAxisTickFont,
  plotlyAxisTitleFont,
} from "./plotlyAxisTheme";
import { plotlyHorizontalLegendTop } from "./plotlyLegendLayout";
import {
  GraphV2FullscreenInlineRow,
  useGraphV2PlotHeight,
} from "../ChartFullscreenShell";
import { useSamplingPlotExportRegister } from "../../graphView/useSamplingPlotExportRegister";
import { getPaletteColorAt } from "../../graphView/colors";
import "./wrVsLengthPlotly.css";
const CARD_W = 280;
const CARD_H = 220;

function axisTitleLarger(text, isMobile) {
  const base = plotlyAxisTitle(text, isMobile);
  return {
    ...base,
    font: { ...plotlyAxisTitleFont(isMobile), size: isMobile ? 16 : 22 },
  };
}

function axisTickLarger(isMobile) {
  return { ...plotlyAxisTickFont(isMobile, "default"), size: isMobile ? 12 : 14 };
}

function buildPopulationTicksWithMin12(maxPctObserved) {
  const maxPct = Number.isFinite(Number(maxPctObserved))
    ? Math.max(0, Number(maxPctObserved))
    : 0;

  const TICK_COUNT = 12;

  // Requested rule:
  // max < 12 => step 1, max < 24 => step 2, ... and so on.
  const step = Math.max(1, Math.ceil(maxPct / 12));

  // Keep exactly 12 tick values on axis; add one-step headroom in range.
  const ticks = Array.from({ length: TICK_COUNT }, (_, i) =>
    Number((i * step).toFixed(1))
  );
  const rangeMax = Number((step * TICK_COUNT).toFixed(1));
  return { ticks, rangeMax };
}

export default function NumberFrequencyVsBinChart({
  graphData,
  selectedUnit,
  isMobile,
  selectedBinSize,
}) {
  const wrapRef = useRef(null);
  const plotRef = useRef(null);
  useSamplingPlotExportRegister(plotRef);
  const [tip, setTip] = useState(null);

  const unitWord = selectedUnit.value === "English" ? "Inches" : "mm";

  const {
    labels,
    numbers,
    percents,
    stockingNums,
    pctStocking,
    yTicks,
    yRangeMax,
    hasStockingBars,
  } = useMemo(() => {
      const bins = processBinData(graphData, {
        unitValue: selectedUnit.value,
        binSizeValue: selectedBinSize?.value,
      });
      const total = graphData.length || 1;
      const labels0 = bins.map((b) => b.binLabel);
      const numbers0 = bins.map((b) => Math.floor(b.number || 0));
      const stockingNums0 = bins.map((b) => Math.floor(b.stocking || 0));
      const samplingNums0 = bins.map((b, i) =>
        Math.max(0, numbers0[i] - stockingNums0[i])
      );
      const percents0 = samplingNums0.map((n) => (n / total) * 100);
      const pctStocking0 = stockingNums0.map((n) => (n / total) * 100);
      const maxPct = [...percents0, ...pctStocking0].reduce((m, v) => {
        const n = Number(v);
        return Number.isFinite(n) && n > m ? n : m;
      }, 0);
      const { ticks: yTicks0, rangeMax } = buildPopulationTicksWithMin12(maxPct);
      const hasStockingBars =
        graphData.some((d) => d.isStocking) ||
        stockingNums0.some((n) => (n || 0) > 0);

      return {
        labels: labels0,
        numbers: numbers0,
        percents: percents0,
        stockingNums: stockingNums0,
        pctStocking: pctStocking0,
        yTicks: yTicks0,
        yRangeMax: rangeMax,
        hasStockingBars,
      };
    }, [graphData, selectedUnit.value, selectedBinSize?.value]);

  const legendLayout = useMemo(
    () =>
      plotlyHorizontalLegendTop(hasStockingBars ? 2 : 1, isMobile, {
        legendLabels: hasStockingBars ? ["Sampling", "Stocked"] : ["Sampling"],
      }),
    [hasStockingBars, isMobile]
  );

  const plotHeight = useGraphV2PlotHeight({
    isMobile,
    mobileDefault: 600,
    desktopDefault: 750,
    legendExtra: legendLayout.extraHeight || 0,
    fullscreenReserved: isMobile ? 260 : 225,
  });

  const onHover = useCallback((eventData) => {
    const pt = eventData?.points?.[0];
    const el = wrapRef.current;
    const evt = eventData?.event;
    if (!pt || el == null) {
      return;
    }
    const traceType = pt.data?.type || pt.fullData?.type;
    if (traceType !== "bar") {
      return;
    }
    const cd = pt.customdata;
    if (!Array.isArray(cd) || cd.length < 3) {
      return;
    }
    const binLabel = String(cd[0] ?? "");
    const count = Number(cd[1]);
    const pctVal = Number(cd[2]);
    const countOther = cd.length >= 5 ? Number(cd[3]) : NaN;
    const pctOther = cd.length >= 5 ? Number(cd[4]) : NaN;
    if (!Number.isFinite(pctVal)) {
      return;
    }
    const traceName = String(pt.data?.name || pt.fullData?.name || "");
    const segment = traceName === "Stocked" ? "Stocked" : "Sampling";

    let cx;
    let cy;
    if (evt && typeof evt.clientX === "number" && typeof evt.clientY === "number") {
      cx = evt.clientX;
      cy = evt.clientY;
    } else if (pt.bbox) {
      cx = (pt.bbox.x0 + pt.bbox.x1) / 2;
      cy = pt.bbox.y0;
    } else {
      return;
    }

    const rect = el.getBoundingClientRect();
    const pad = 12;
    let left = cx - rect.left + pad;
    let top = cy - rect.top + pad;
    if (left + CARD_W > rect.width - 8) {
      left = cx - rect.left - CARD_W - pad;
    }
    if (top + CARD_H > rect.height - 8) {
      top = cy - rect.top - CARD_H - pad;
    }
    left = Math.max(8, Math.min(left, rect.width - CARD_W - 8));
    top = Math.max(8, Math.min(top, rect.height - CARD_H - 8));

    setTip({
      left,
      top,
      binLabel,
      count,
      pctVal,
      segment,
      countOther,
      pctOther,
    });
  }, []);

  const onUnhover = useCallback(() => setTip(null), []);

  return (
    <div
      ref={wrapRef}
      className="number-frequency-vs-bin-plotly-wrap"
      style={{ position: "relative", width: "100%" }}
    >
      <GraphV2FullscreenInlineRow />
      <Plot
        ref={plotRef}
        data={[
          {
            type: "bar",
            name: "Sampling",
            x: labels,
            y: percents,
            marker: { color: getPaletteColorAt(0) },
            customdata: labels.map((bl, i) => [
              bl,
              numbers[i] - (stockingNums[i] || 0),
              percents[i],
              stockingNums[i] || 0,
              pctStocking[i] || 0,
            ]),
            hovertemplate: "<extra></extra>",
          },
          ...(hasStockingBars
            ? [
                {
                  type: "bar",
                  name: "Stocked",
                  x: labels,
                  y: pctStocking,
                  marker: {
                    color: getPaletteColorAt(3),
                    line: { color: getPaletteColorAt(0), width: 1 },
                  },
                  customdata: labels.map((bl, i) => [
                    bl,
                    stockingNums[i] || 0,
                    pctStocking[i] || 0,
                    numbers[i] - (stockingNums[i] || 0),
                    percents[i],
                  ]),
                  hovertemplate: "<extra></extra>",
                },
              ]
            : []),
        ]}
        layout={{
          autosize: true,
          height: plotHeight,
          margin: { t: legendLayout.marginTop, r: 40, b: isMobile ? 160 : 140, l: 80 },
          paper_bgcolor: "white",
          plot_bgcolor: "white",
          barmode: "stack",
          showlegend: true,
          legend: legendLayout.legend,
          xaxis: {
            title: axisTitleLarger(
              `Length Bin (${selectedUnit.value === "English" ? "Inches" : "mm"})`,
              isMobile
            ),
            tickangle: -90,
            ...plotlyAxisBaseCategoricalX(isMobile),
            tickfont: axisTickLarger(isMobile),
          },
          yaxis: {
            title: axisTitleLarger("Percentage of Population", isMobile),
            rangemode: "tozero",
            tickmode: "array",
            tickvals: yTicks,
            range: [0, yRangeMax || 30],
            ticksuffix: "%",
            tickformat: ".1f",
            ...plotlyAxisBase(isMobile),
            tickfont: axisTickLarger(isMobile),
          },
          hovermode: "closest",
        }}
        config={{ responsive: true, displaylogo: false }}
        style={{ width: "100%" }}
        useResizeHandler
        onHover={onHover}
        onUnhover={onUnhover}
      />
      {tip && (
        <div
          className={`wr-length-hover-card wr-length-hover-card--nf-bin${
            isMobile ? " wr-length-hover-card--mobile" : ""
          }`}
          style={{ left: tip.left, top: tip.top }}
          role="tooltip"
        >
          <div className="wr-length-hover-card__body">
            <p className="wr-length-hover-card__title wr-length-hover-card__label-with-swatch">
              <span
                className="wr-length-hover-card__dot-swatch"
                style={{
                  backgroundColor:
                    tip.segment === "Stocked"
                      ? getPaletteColorAt(3)
                      : getPaletteColorAt(0),
                }}
                aria-hidden
              />
              Population by bin — {tip.segment}
            </p>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">Length bin:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--length">
                {tip.binLabel} {unitWord}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">This segment:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--actual">
                {Number.isFinite(tip.count) ? String(Math.round(tip.count)) : "—"} fish (
                {tip.pctVal.toFixed(1)}%)
              </span>
            </div>
            {Number.isFinite(tip.countOther) && tip.countOther > 0 && (
              <div className="wr-length-hover-card__row">
                <span className="wr-length-hover-card__label">Other segment:</span>
                <span className="wr-length-hover-card__value wr-length-hover-card__value--standard">
                  {Math.round(tip.countOther)} fish (
                  {Number.isFinite(tip.pctOther) ? tip.pctOther.toFixed(1) : "—"}%)
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
